import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import HistoryIcon from "@mui/icons-material/History";
import JSZip from "jszip";
import scriptRunnerV2Service from "../../../services/scriptRunnerV2.service";
import useScriptPermissions from "../hooks/useScriptPermissions";
import styles from "./Workspace.module.css";

// interns scriptrunner - Main Workspace for editing script files within a ZIP
const ScriptWorkspaceV2 = () => {
  const { scriptId, version } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useScriptPermissions();

  // interns scriptrunner - redirect if no write permission
  useEffect(() => {
    if (!canWrite) {
      navigate("/script-runner-V2");
    }
  }, [canWrite, navigate]);

  // interns scriptrunner - State for script and file data
  const [script, setScript] = useState(null);
  const [files, setFiles] = useState({}); // { path: { content: string, isBinary: bool } }
  const [activeFile, setActiveFile] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [asyncSaving, setAsyncSaving] = useState(false);

  // interns scriptrunner - Version control state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [commitMessage, setCommitMessage] = useState("");
  const [newVersion, setNewVersion] = useState("");
  const [incrementType, setIncrementType] = useState("patch"); // "patch", "minor", "major"

  const zipRef = useRef(null);

  // interns scriptrunner - Function to calculate next version based on type
  const calculateNextVersion = useCallback((current, type) => {
    if (!current) return "1.0.0";
    const parts = current.split(".").map(Number);
    while (parts.length < 3) parts.push(0);

    if (type === "major") {
      parts[0] += 1;
      parts[1] = 0;
      parts[2] = 0;
    } else if (type === "minor") {
      parts[1] += 1;
      parts[2] = 0;
    } else {
      parts[2] += 1;
    }
    return parts.join(".");
  }, []);

  // Update newVersion when incrementType changes
  useEffect(() => {
    setNewVersion(calculateNextVersion(version, incrementType));
  }, [version, incrementType, calculateNextVersion]);

  // interns scriptrunner - Initial load: Fetch metadata and extract ZIP
  const loadWorkspace = useCallback(async () => {
    try {
      setLoading(true);
      console.log(
        "interns scriptrunner - Loading workspace for script:",
        scriptId,
        "version:",
        version,
      );

      const scriptData = await scriptRunnerV2Service.getScriptById(scriptId);
      setScript(scriptData);

      // interns scriptrunner - Download and extract ZIP content
      const zipData = await scriptRunnerV2Service.downloadZip(
        scriptId,
        version,
      );

      if (!zipData || zipData.byteLength === 0) {
        throw new Error("Downloaded ZIP is empty or invalid");
      }

      const zip = await JSZip.loadAsync(zipData);
      zipRef.current = zip;

      const extractedFiles = {};
      const filePromises = [];

      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          filePromises.push(
            file.async("string").then((content) => {
              extractedFiles[relativePath] = {
                content: content,
                isBinary: false,
              };
            }),
          );
        }
      });

      await Promise.all(filePromises);

      if (Object.keys(extractedFiles).length === 0) {
        throw new Error("No files found in the script ZIP");
      }

      setFiles(extractedFiles);

      // interns scriptrunner - Set entrypoint or default active file
      const entrypoint = extractedFiles["main.py"]
        ? "main.py"
        : Object.keys(extractedFiles)[0];

      if (entrypoint) {
        setActiveFile(entrypoint);
        setEditorContent(extractedFiles[entrypoint].content);
      }
    } catch (error) {
      console.error("interns scriptrunner - Workspace load failed:", error);
      alert("Failed to load workspace.");
      navigate("/script-runner-V2/view");
    } finally {
      setLoading(false);
    }
  }, [scriptId, version, navigate]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  // interns scriptrunner - Switch between files in the explorer
  const handleFileClick = (path) => {
    if (activeFile) {
      setFiles((prev) => ({
        ...prev,
        [activeFile]: { ...prev[activeFile], content: editorContent },
      }));
    }
    setActiveFile(path);
    setEditorContent(files[path].content);
  };

  // interns scriptrunner - Package files back into ZIP and upload as new version
  const handleCommit = async () => {
    setAsyncSaving(true);
    try {
      const zip = new JSZip();

      // interns scriptrunner - Ensure current editor content is included
      const finalFiles = {
        ...files,
        [activeFile]: { ...files[activeFile], content: editorContent },
      };

      let extractedSchema = null;
      Object.keys(finalFiles).forEach((path) => {
        zip.file(path, finalFiles[path].content);

        // interns scriptrunner - detect template.json and parse it for explicit schema upload
        if (path.endsWith("template.json")) {
          try {
            extractedSchema = JSON.stringify(
              JSON.parse(finalFiles[path].content),
            );
          } catch (e) {
            console.warn(
              "interns scriptrunner - template.json exists but is invalid JSON",
              e,
            );
          }
        }
      });

      const blob = await zip.generateAsync({ type: "blob" });
      const formData = new FormData();
      formData.append("file", blob, "script.zip");
      formData.append("version", newVersion);
      formData.append("changeSummary", commitMessage);
      formData.append("isMajorUpdate", incrementType === "major");
      formData.append("isMinorUpdate", incrementType === "minor");

      // interns scriptrunner - reuse existing data
      formData.append("entrypoint", "main.py");

      // interns scriptrunner - Send explicit schema if found in workspace files
      if (extractedSchema) {
        formData.append("inputSchema", extractedSchema);
        console.log(
          "interns scriptrunner - Found template.json, sending as explicit inputSchema",
        );
      }

      await scriptRunnerV2Service.uploadVersion(scriptId, formData);

      setShowSaveDialog(false);
      alert(`Version ${newVersion} committed successfully!`);
      navigate(`/script-runner-V2/edit/${scriptId}/${newVersion}`);
    } catch (error) {
      console.error("interns scriptrunner - Commit failed", error);
      alert("Failed to save new version.");
    } finally {
      setAsyncSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={styles.loadingOverlay}>
        <CircularProgress color="primary" />
        <Typography sx={{ mt: 2 }}>Extracting Script Workspace...</Typography>
      </Box>
    );
  }

  return (
    <div className={styles.workspaceContainer}>
      {/* interns scriptrunner - Top Header Bar */}
      <div className={styles.topBar}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={() => navigate("/script-runner-V2/view")}
            sx={{ color: "#fff", mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {script?.name}{" "}
            <span style={{ color: "#858585", fontWeight: "normal" }}>
              — v{version}
            </span>
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={() => setShowSaveDialog(true)}
          sx={{ bgcolor: "#0e639c", "&:hover": { bgcolor: "#1177bb" } }}
        >
          Commit Changes
        </Button>
      </div>

      <div className={styles.mainContent}>
        {/* interns scriptrunner - Left Sidebar: File Explorer */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>Explorer</div>
          <div className={styles.fileList}>
            {Object.keys(files)
              .sort()
              .map((path) => (
                <div
                  key={path}
                  className={`${styles.fileItem} ${activeFile === path ? styles.activeFile : ""}`}
                  onClick={() => handleFileClick(path)}
                >
                  <InsertDriveFileIcon
                    className={styles.fileIcon}
                    sx={{ color: path.endsWith(".py") ? "#3572A5" : "#f1e05a" }}
                  />
                  {path}
                </div>
              ))}
          </div>
        </div>

        {/* interns scriptrunner - Center: Code Editor Area */}
        <div className={styles.editorArea}>
          <div className={styles.editorHeader}>
            <InsertDriveFileIcon sx={{ fontSize: 14, mr: 1 }} />
            {activeFile}
          </div>
          <textarea
            className={styles.codeArea}
            value={editorContent}
            onChange={(e) => setEditorContent(e.target.value)}
            spellCheck="false"
          />
        </div>

        {/* interns scriptrunner - Right Sidebar: History / Info */}
        <div className={styles.versionSidebar}>
          <div className={styles.sidebarHeader}>
            <HistoryIcon
              sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }}
            />
            Quick Info
          </div>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              display="block"
              sx={{ color: "#858585", mb: 1 }}
            >
              Current Version: {version}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{ color: "#858585" }}
            >
              Last modified: {new Date(script?.updatedAt).toLocaleString()}
            </Typography>
            <Divider sx={{ my: 2, bgcolor: "#3c3c3c" }} />
            <Typography variant="caption" sx={{ color: "#858585" }}>
              Commit changes to create a new semantic version.
            </Typography>
          </Box>
        </div>
      </div>

      {/* interns scriptrunner - Save/Commit Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => !asyncSaving && setShowSaveDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Commit Changes</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              Update Type
            </Typography>
            <RadioGroup
              row
              value={incrementType}
              onChange={(e) => setIncrementType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="patch"
                control={<Radio size="small" />}
                label="Patch"
              />
              <FormControlLabel
                value="minor"
                control={<Radio size="small" />}
                label="Minor"
              />
              <FormControlLabel
                value="major"
                control={<Radio size="small" />}
                label="Major"
              />
            </RadioGroup>

            <TextField
              fullWidth
              label="New Version"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="What did you change?"
              multiline
              rows={3}
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="e.g. Updated main.py logic"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowSaveDialog(false)} disabled={asyncSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommit}
            disabled={asyncSaving || !commitMessage || !newVersion}
            startIcon={asyncSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Commit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScriptWorkspaceV2;
